import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  designation: string | null;
  organization: string | null;
  experience: number | null;
  specializations: string[] | null;
  address: string | null;
  website: string | null;
  instagram: string | null;
  avatar_url: string | null;
  whatsapp_connected: boolean;
  whatsapp_phone_number: string | null;
  whatsapp_last_connected_at: string | null;

  // Payment details
  payment_upi_id: string | null;
  payment_qr_code_url: string | null;
  payment_bank_details: string | null;

  created_at: string;
  updated_at: string;
}

export interface ProfileUpdateData {
  name?: string;
  phone?: string;
  designation?: string;
  organization?: string;
  experience?: number;
  specializations?: string[];
  address?: string;
  website?: string;
  instagram?: string;
  avatar_url?: string;

  // Payment details
  payment_upi_id?: string | null;
  payment_bank_details?: string | null;
  // Note: payment_qr_code_url handled via separate upload endpoint
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // Fetch user profile from database
  const fetchProfile = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Update user profile
  const updateProfile = async (updates: ProfileUpdateData): Promise<boolean> => {
    if (!user) {
      setError('User not authenticated');
      return false;
    }

    try {
      setUpdating(true);
      setError(null);

      const { data, error: updateError } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      setProfile(data);
      return true;
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  // Fetch profile on mount and when user changes
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    updating,
    updateProfile,
    refetch: fetchProfile,
  };
}
